import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

const MERGE_FIELDS = ["{first_name}", "{last_name}", "{assessment_date}", "{assessment_time}", "{location}", "{address}", "{contact_name}", "{contact_number}", "{programme_name}", "{arrival_minutes}"];
const EMPTY = { name: "", subject: "", body: "", kind: "operational" };

export default function Templates() {
  const { user } = useAuth();
  const isManagement = user?.role === "management_admin";
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/templates").then((r) => setTemplates(r.data)).catch(() => toast.error("Could not load templates"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name || !editing.body) { toast.error("Name and body are required."); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/templates/${editing.id}`, { name: editing.name, subject: editing.subject, body: editing.body, kind: editing.kind });
      else await api.post("/admin/templates", { name: editing.name, subject: editing.subject, body: editing.body, kind: editing.kind });
      toast.success("Template saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!templates) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="templates-loading" />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="templates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Admin</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="templates-heading">Communication Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Merge fields are filled automatically with candidate and assessment-day details.</p>
        </div>
        {isManagement && (
          <Button onClick={() => setEditing({ ...EMPTY })} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="add-template-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Template
          </Button>
        )}
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="merge-fields-help">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 self-center">Merge fields:</span>
        {MERGE_FIELDS.map((f) => <code key={f} className="text-[10px] font-mono bg-elevated border border-steel rounded-sm px-1.5 py-0.5 text-slate-600">{f}</code>)}
      </div>

      <div className="space-y-3" data-testid="templates-list">
        {templates.map((t) => (
          <div key={t.id} className="bg-white border border-steel rounded-md p-5" data-testid={`template-${t.key}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal">{t.name}</h2>
                  <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-widest ${t.kind === "marketing" ? "text-purple-800 bg-purple-100 border-purple-300" : "text-sky-800 bg-sky-100 border-sky-300"}`}>
                    {t.kind}
                  </Badge>
                </div>
                {t.subject && <p className="text-xs text-slate-500 mt-1">Subject: {t.subject}</p>}
                <pre className="text-xs text-slate-600 mt-2 whitespace-pre-wrap font-sans leading-relaxed bg-elevated rounded-sm p-3">{t.body}</pre>
              </div>
              {isManagement && (
                <Button variant="outline" size="icon" onClick={() => setEditing({ ...t })} className="border-steel text-charcoal bg-white shrink-0 h-9 w-9" data-testid={`edit-template-${t.key}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-white border-steel text-foreground max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">{editing?.id ? "Edit Template" : "New Template"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-white border-steel text-charcoal" data-testid="tpl-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-600 text-sm">Type</Label>
                  <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                    <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="tpl-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operational">Operational / transactional</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Subject</Label>
                <Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="bg-white border-steel text-charcoal" data-testid="tpl-subject" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-600 text-sm">Body</Label>
                <Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="bg-white border-steel text-charcoal min-h-48 font-mono text-sm" data-testid="tpl-body" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-steel text-charcoal bg-white" data-testid="tpl-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="tpl-save">{saving ? "Saving..." : "Save Template"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
